import React, { useState } from 'react';
import {
  useGetSolutionTemplatesQuery,
  useCreateSolutionTemplateMutation,
  useUpdateSolutionTemplateMutation,
  useDeleteSolutionTemplateMutation,
  SolutionTemplate,
} from '../store/apiSlice';
import { useNotification } from '../contexts/NotificationContext';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import './Solutions.css';

const Solutions: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const { data: solutionsResponse, isLoading: initialLoading } = useGetSolutionTemplatesQuery();
  const [createSolution, { isLoading: creating }] = useCreateSolutionTemplateMutation();
  const [updateSolution, { isLoading: updating }] = useUpdateSolutionTemplateMutation();
  const [deleteSolution, { isLoading: deleting }] = useDeleteSolutionTemplateMutation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSolution, setEditingSolution] = useState<SolutionTemplate | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [solutionToDelete, setSolutionToDelete] = useState<SolutionTemplate | null>(null);
  const [formData, setFormData] = useState({ code: '', name: '' });

  const solutions = solutionsResponse?.data || [];
  const actionLoading = creating || updating || deleting;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.code.trim() || !formData.name.trim()) return;

    try {
      await createSolution({ code: formData.code.trim(), name: formData.name.trim() }).unwrap();
      setFormData({ code: '', name: '' });
      setShowCreateForm(false);
      showSuccess('Solution template created successfully');
    } catch (error: any) {
      console.error('Error creating solution:', error);
      const errorMessage = error.data?.message || error.message || 'Failed to create solution template';
      showError(errorMessage);
    }
  };

  const handleEdit = (solution: SolutionTemplate) => {
    setEditingSolution(solution);
    setFormData({ code: solution.code, name: solution.name });
    setShowCreateForm(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSolution || !formData.name.trim()) return;

    try {
      await updateSolution({ code: editingSolution.code, name: formData.name.trim() }).unwrap();
      setFormData({ code: '', name: '' });
      setEditingSolution(null);
      setShowCreateForm(false);
      showSuccess('Solution template updated successfully');
    } catch (error: any) {
      console.error('Error updating solution:', error);
      const errorMessage = error.data?.message || error.message || 'Failed to update solution template';
      showError(errorMessage);
    }
  };

  const handleDeleteClick = (solution: SolutionTemplate) => {
    setSolutionToDelete(solution);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!solutionToDelete) return;

    setShowDeleteDialog(false);
    try {
      await deleteSolution(solutionToDelete.code).unwrap();
      showSuccess('Solution template deleted successfully');
      setSolutionToDelete(null);
    } catch (error: any) {
      console.error('Error deleting solution:', error);
      const errorMessage = error.data?.message || error.message || 'Failed to delete solution template';
      showError(errorMessage);
      setSolutionToDelete(null);
    }
  };

  const handleCancel = () => {
    setFormData({ code: '', name: '' });
    setEditingSolution(null);
    setShowCreateForm(false);
  };

  return (
    <Layout>
      <div className="solutions-page">
        <header className="solutions-header">
          <h1>Solution Templates</h1>
          <button
            onClick={() => {
              setEditingSolution(null);
              setFormData({ code: '', name: '' });
              setShowCreateForm(true);
            }}
            className="create-button"
            disabled={actionLoading}
          >
            + Create Solution Template
          </button>
        </header>

        {showCreateForm && (
          <div className="create-form">
            <h2>{editingSolution ? 'Edit Solution Template' : 'Create Solution Template'}</h2>
            <form onSubmit={editingSolution ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label htmlFor="code">Solution Code *</label>
                <input
                  id="code"
                  type="text"
                  placeholder="e.g., myena"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                  disabled={!!editingSolution || actionLoading} // Code cannot be changed when editing
                />
                {editingSolution && (
                  <small style={{ color: '#666' }}>Code cannot be changed</small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="name">Solution Name *</label>
                <input
                  id="name"
                  type="text"
                  placeholder="e.g., Myena HR System"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={actionLoading}
                />
              </div>
              <div className="form-actions">
                <button type="submit" disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : editingSolution ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={handleCancel} disabled={actionLoading}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="solutions-list">
          {initialLoading && solutions.length === 0 ? (
            <div className="loading-indicator">Loading solutions...</div>
          ) : solutions.length === 0 ? (
            <p>No solution templates yet. Create your first solution template!</p>
          ) : (
            <table className="solutions-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {solutions.map((solution) => (
                  <tr key={solution.code}>
                    <td>
                      <code>{solution.code}</code>
                    </td>
                    <td>{solution.name}</td>
                    <td>
                      {solution.createdAt
                        ? new Date(solution.createdAt).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(solution)}
                          className="edit-button"
                          disabled={actionLoading}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(solution)}
                          className="delete-button"
                          disabled={actionLoading}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <ConfirmDialog
          isOpen={showDeleteDialog}
          title="Delete Solution Template"
          message={`Are you sure you want to delete "${solutionToDelete?.name}" (${solutionToDelete?.code})? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setSolutionToDelete(null);
          }}
        />
      </div>
    </Layout>
  );
};

export default Solutions;

